import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { AlertService } from "../services/alert.service";
import { EnvService } from "../services/env.service";
import { MatTableDataSource } from "@angular/material/table";
import { LookupService } from "../srvs/lookup";
import { Subscription } from "rxjs";

@Component({
  selector: "micro-cmdb-application-detail",
  templateUrl: "./cmdbApplication-detail.component.html",
})
export class CmdbApplicationDetailComponent implements OnInit {
  dataSource = new MatTableDataSource<any>();
  @Input() applicationId: any;
  @Output() persisted = new EventEmitter<any>();
  application: any;
  contactOptions: any[] = [];
  departmentOptions: any[] = [];

  // Subscription to listen for LookupService events
  private lookupSub!: Subscription;
  isLoading: boolean = true;

  constructor(
    private env: EnvService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private lookupService: LookupService,
  ) {}

  ngOnInit(): void {
    if (this.applicationId) {
      this.load(this.applicationId);
    } else {
      this.route.params.subscribe((params: Params) => {
        params["id"] === "0" ? this.createNew() : this.load(params["id"]);
      });
    }

    // Try loading contacts and department contacts immediately, if the lookup service has already loaded them
    this.getContacts();
    this.getDepartmentContacts();

    if (
      this.contactOptions.length === 0 ||
      this.departmentOptions.length === 0
    ) {
      this.lookupSub = this.lookupService.lookupEvents.subscribe((event) => {
        try {
          if (event === "load") {
            this.getContacts();
            this.getDepartmentContacts();
          }
        } finally {
          this.isLoading = false;
        }
      });
    } else this.isLoading = false
  }

  ngOnDestroy() {
    // Unsubscribe when the component unmounts
    if (this.lookupSub) {
      this.lookupSub.unsubscribe();
    }
  }

  load(id: string): void {
    this.http
      .get(`${this.env.e.url}/domain/cmdbApplications/${id}`)
      .subscribe(async (data) => {
        this.application = data;

        // Load the application tag name for the application
        this.loadApplicationTagName(this.application);
      });
  }

  private loadApplicationTagName(application: any): void {
    const tagId = application.applicationTagId;
    if (tagId) {
      this.http
        .get(`${this.env.e.url}/domain/cmdbApplicationTags/${tagId}`)
        .subscribe({
          next: (applicationTag: any) => {
            this.application.applicationTagName = applicationTag
              ? applicationTag.name
              : "";
            this.dataSource.data = [...this.dataSource.data];
          },
          error: () => {
            this.application.applicationTagName = "";
            this.dataSource.data = [...this.dataSource.data];
          },
        });
    }
  }

  createNew(): void {
    this.application = {
      name: "",
      businessCriticality: "Unknown",
      operationalStatus: "Unknown",
      assetTag: "Unknown",
      description: "",
      applicationTagId: "",
      tbd: 0,
    };
  }

  save(): void {
    const isUpdate = !!this.application.id;
    const request = isUpdate
      ? this.http.put(
          `${this.env.e.url}/domain/cmdbApplications/${this.application.id}`,
          this.application,
        )
      : this.http.post(
          `${this.env.e.url}/domain/cmdbApplications`,
          this.application,
        );
    request.subscribe((data) => {
      this.application = data;
      this.alertService.info(
        `${isUpdate ? "Updated" : "Created"} ${this.application.name}`,
      );
      this.persisted.emit({ data, action: isUpdate ? "updated" : "added" });
    });
  }

  delete(): void {
    this.http
      .delete(
        `${this.env.e.url}/domain/cmdbApplications/${this.application.id}`,
      )
      .subscribe(() => {
        this.persisted.emit({ data: this.application, action: "deleted" });
        this.alertService.info(`Deleted ${this.application.name}`);
        if (!this.applicationId) {
          this.router.navigate(["/cmdbApplications"]);
        }
      });
  }

  private getContacts(): void {
    const data = this.lookupService.getOptions("auth", "contacts");

    this.contactOptions = [];
    for (const [key, value] of Object.entries(data)) {
      this.contactOptions.push({ id: key, name: value });
    }
  }

  private getDepartmentContacts(): void {
    const data = this.lookupService.getOptions("auth", "contactDepartments");

    this.departmentOptions = [];
    for (const [key, value] of Object.entries(data)) {
      this.departmentOptions.push({ id: key, name: value });
    }
  }

  onChange(event: any): void {}
}
